import * as React from 'react';
import {
  MembershipChangeAction,
  WithGroup,
  WithGroupProps,
  withMembershipChangeAction,
  withSiteMembers,
} from '../Context';
import { InjectedTranslateProps, translate } from 'react-i18next';
import {
  isAdmin,
  isGroupPrivate,
  canJoinGroup,
  isProfilePublic,
} from '@wix/social-groups-api';
import { ApiTypes } from '@wix/social-groups-api/dist/src/types';
import { Button, PRIORITY } from 'wix-ui-tpa/Button';
import { ProfilePrivacyDialog } from '@wix/social-groups-common';
import { compose } from '@wix/social-groups-common/dist/src/compose';
import {
  WixSiteMember,
  WixSiteMemberActions,
} from '@wix/social-groups-common/dist/src/context';

import { DATA_HOOKS } from './dataHooks';

import styles from './MembershipButton.st.css';

interface MembershipButtonConfig {
  buttonLabelKey: string;
  priority: PRIORITY;
  onClick?(): void;
}

const getMembershipButtonPreferences = (
  group: ApiTypes.v1.GroupResponse,
): MembershipButtonConfig => {
  const joinLabel =
    isAdmin(group) || !isGroupPrivate(group)
      ? 'groups-web.join'
      : 'groups-web.request-to-join';

  const config: {
    [key in ApiTypes.v1.RelationshipWithGroup]: MembershipButtonConfig;
  } = {
    [ApiTypes.v1.RelationshipWithGroup.NONE]: {
      buttonLabelKey: joinLabel,
      priority: PRIORITY.primary,
    },
    [ApiTypes.v1.RelationshipWithGroup.PENDING_APPROVAL]: {
      buttonLabelKey: 'groups-web.pending',
      priority: PRIORITY.secondary,
    },
    [ApiTypes.v1.RelationshipWithGroup.JOINED]: {
      buttonLabelKey: 'groups-web.joined',
      priority: PRIORITY.secondary,
    },
    [ApiTypes.v1.RelationshipWithGroup.REJECTED_MEMBERSHIP]: {
      buttonLabelKey: joinLabel,
      priority: PRIORITY.primary,
    },
  };
  return config[group.relationship];
};

export interface MembershipButtonProps {
  className?: string;
  biOrigin?: string;
}

type Props = WithGroupProps &
  InjectedTranslateProps &
  WixSiteMember &
  WixSiteMemberActions &
  MembershipChangeAction &
  MembershipButtonProps;

class MembershipButtonComponent extends React.Component<Props> {
  state = {
    isProfileModalOpen: false,
  };

  componentDidUpdate(
    prevProps: Readonly<Props>,
    prevState: Readonly<Props>,
  ): void {
    if (
      this.isProfilePrivate(prevProps.currentSiteMember) &&
      !this.isProfilePrivate(this.props.currentSiteMember) &&
      this.state.isProfileModalOpen
    ) {
      this.handleClick(null);
    }
  }

  render() {
    const { t, group, currentSiteMember } = this.props;
    if (!canJoinGroup(group)) {
      return null;
    }

    const { buttonLabelKey, priority } = getMembershipButtonPreferences(group);
    return (
      <div {...styles('root')}>
        <Button
          priority={priority}
          onClick={this.handleClick}
          data-hook={DATA_HOOKS.membershipButton}
          fullWidth={true}
        >
          {t(buttonLabelKey)}
        </Button>
        {this.isProfilePrivate(currentSiteMember) && (
          <ProfilePrivacyDialog
            onRequestClose={this.closeProfileModal}
            onChangeProfile={this.makeProfilePublic}
            isOpen={this.state.isProfileModalOpen}
          />
        )}
      </div>
    );
  }

  private readonly handleClick = e => {
    e && e.stopPropagation();
    const { currentSiteMember } = this.props;
    if (this.isProfilePrivate(currentSiteMember)) {
      this.openProfileModal();
      return;
    }
    const { biOrigin, changeMembership } = this.props;
    changeMembership(biOrigin);
  };

  private isProfilePrivate(currentSiteMember) {
    return currentSiteMember && !isProfilePublic(currentSiteMember);
  }

  private readonly closeProfileModal = () => {
    this.setState({ isProfileModalOpen: false });
  };

  private readonly openProfileModal = () => {
    this.setState({ isProfileModalOpen: true });
  };

  private readonly makeProfilePublic = () => {
    this.props.makeProfilePublic(this.props.currentSiteMember.id);
  };
}

const enhance = compose(
  translate(),
  WithGroup,
  withSiteMembers,
  withMembershipChangeAction,
);

export const MembershipButton: React.FunctionComponent<MembershipButtonProps> = enhance(
  MembershipButtonComponent,
);

MembershipButton.displayName = 'MembershipButton';
